// Typed fetch wrappers for the /api/* endpoints. All requests are POST,
// JSON in/out, same-origin (so no CORS, no credentials flag needed -
// cookies travel automatically with same-origin POSTs).

export interface FitResult {
  department: string;
  whatDisappears: string;
  whatYouCantDo: string;
  whoHasAuthority: string;
}

export interface FitResponse {
  result: FitResult;
  jobTitle: string;
}

export interface UnlockEmailResponse {
  ok: true;
  calendlyUrl: string;
}

export interface SelectedLens {
  id: number;
  name: string;
  rationale: string;
  whatItReveals: string;
}

export interface SelectModelsResponse {
  selected: SelectedLens[];
}

export interface ApiError {
  code: string;
  message: string;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try {
      const data = (await res.json()) as { error?: ApiError };
      if (data.error?.message) msg = data.error.message;
    } catch {
      // body wasn't JSON; keep generic
    }
    throw new Error(msg);
  }

  return (await res.json()) as T;
}

export function postFit(jobTitle: string): Promise<FitResponse> {
  return postJson<FitResponse>('/api/fit', { jobTitle });
}

export function unlockEmail(
  email: string,
  turnstileToken: string
): Promise<UnlockEmailResponse> {
  return postJson<UnlockEmailResponse>('/api/unlock-email', {
    email,
    turnstileToken,
  });
}

export function postSelectModels(input: {
  orgChart: string;
  diagnosis: string;
  jobTitle?: string;
  fitResult?: FitResult;
}): Promise<SelectModelsResponse> {
  return postJson<SelectModelsResponse>('/api/select-models', input);
}

// ─── Streaming diagnose ───────────────────────────────────────────────────
// fetch() doesn't give us SSE parsing for free; we read the raw body and
// split on "\n\n". Each event we care about looks like:
//   event: delta\ndata: {"text":"..."}\n\n
//   event: done\ndata: {}\n\n
//   event: error\ndata: {"message":"..."}\n\n

export interface DiagnoseStreamCallbacks {
  onDelta: (text: string) => void;
  onDone: () => void;
  onError: (message: string) => void;
}

export async function streamDiagnose(
  orgChart: string,
  callbacks: DiagnoseStreamCallbacks
): Promise<void> {
  let res: Response;
  try {
    res = await fetch('/api/diagnose', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ orgChart }),
    });
  } catch (err) {
    callbacks.onError(err instanceof Error ? err.message : 'Network error.');
    return;
  }

  if (!res.ok || !res.body) {
    let msg = `Request failed (${res.status})`;
    try {
      const data = (await res.json()) as { error?: ApiError };
      if (data.error?.message) msg = data.error.message;
    } catch {
      // body wasn't JSON
    }
    callbacks.onError(msg);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Process complete events (separated by blank line). Whatever's left
      // after the last \n\n stays in the buffer for the next chunk.
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        handleSseMessage(raw, callbacks);
      }
    }
  } catch (err) {
    callbacks.onError(err instanceof Error ? err.message : 'Stream interrupted.');
  }
}

function handleSseMessage(raw: string, cb: DiagnoseStreamCallbacks): void {
  let event = 'message';
  let data = '';
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data += line.slice(5).trim();
  }
  if (!data) return;

  try {
    const parsed = JSON.parse(data) as { text?: string; message?: string };
    if (event === 'delta' && typeof parsed.text === 'string') {
      cb.onDelta(parsed.text);
    } else if (event === 'done') {
      cb.onDone();
    } else if (event === 'error') {
      cb.onError(parsed.message ?? 'Diagnosis failed.');
    }
  } catch {
    // Malformed event - ignore rather than crash the stream.
  }
}
